import { useEffect, useState } from 'react'

import type { Pull, Rarity } from '../lib/api'
import { cardBackUrl } from '../lib/card-back'
import { compactNumber, multiple, quantity, usd } from '../lib/format'
import { AssetLogo } from './AssetLogo'
import { Delta, KindBadge, RarityChip, rarityClass } from './ui'

/**
 * One revealed card.
 *
 * The hierarchy is deliberate and reads top to bottom as three questions:
 * *what is it* (logo, ticker, name), *what did I get* (quantity), *what is it
 * worth* (value, and the multiple that says whether that is good). The multiple
 * carries the rarity colour because it is the number people are actually
 * reading; everything else stays in greys so it has somewhere to stand out
 * from.
 */
export function CardFace({ pull, landed = false }: { pull: Pull; landed?: boolean }) {
  const foil = pull.rarity === 'legendary' || pull.rarity === 'mythic'
  const up = pull.asset.change24h >= 0

  return (
    <div
      className={`card-face ${rarityClass(pull.rarity)} ${foil ? 'card-foil' : ''} ${
        landed ? 'land' : ''
      } flex size-full flex-col`}
    >
      {/* A wash of the rarity behind the mark, so a mythic reads as a mythic
          from across the room before any of the text is legible. */}
      <div className="card-face__wash" aria-hidden />

      <header className="relative flex items-start justify-between gap-2 px-3 pt-3">
        <RarityChip rarity={pull.rarity} />
        <KindBadge kind={pull.asset.kind} />
      </header>

      <div className="relative flex flex-col items-center px-3 pt-3">
        <AssetLogo kind={pull.asset.kind} symbol={pull.asset.symbol} rarity={pull.rarity} size={52} />

        <div className="mt-2.5 w-full text-center">
          <div className="truncate text-lg leading-tight font-bold tracking-tight">
            {pull.asset.symbol}
          </div>
          <div
            className="mt-0.5 truncate text-[0.625rem] leading-tight"
            style={{ color: 'var(--color-muted)' }}
            title={pull.asset.name}
          >
            {pull.asset.name}
          </div>
        </div>
      </div>

      <div className="relative mt-auto px-3 pb-3">
        <div
          className="mb-2 flex items-baseline justify-between gap-2 border-t pt-2 text-[0.625rem]"
          style={{ color: 'var(--color-faint)' }}
        >
          <span className="tnum truncate">
            {quantity(pull.quantity)} {pull.asset.kind === 'stock' ? 'sh' : pull.asset.symbol}
          </span>
          <span className="tnum shrink-0">@ {usd(pull.asset.price)}</span>
        </div>

        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="tnum truncate text-xl leading-none font-bold">{usd(pull.value)}</div>
            <div className="mt-1 flex items-center gap-1.5">
              <Delta value={pull.asset.change24h} />
              <span
                className="text-[0.5625rem]"
                style={{ color: 'var(--color-faint)' }}
                aria-hidden
              >
                24h
              </span>
            </div>
          </div>

          <div className="shrink-0 text-right">
            <div
              className="tnum text-sm leading-none font-bold"
              style={{ color: 'var(--rarity)' }}
            >
              {multiple(pull.multiple)}
            </div>
            {pull.asset.marketCap ? (
              <div className="tnum mt-1 text-[0.5625rem]" style={{ color: 'var(--color-faint)' }}>
                {compactNumber(pull.asset.marketCap)} cap
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Keeps the up/down read available without relying on colour alone. */}
      <span className="sr-only">{up ? 'up' : 'down'} over 24 hours</span>
    </div>
  )
}

/**
 * The back of a card.
 *
 * The artwork is the *same canvas* the 3D card slab is textured with, so the
 * moment the slab hands over to this element mid-slide there is nothing to
 * notice. Painting it once and using it in both places is the only way that
 * stays true — the two were separate drawings and had drifted into completely
 * different designs.
 *
 * `.card-back` keeps a ground and a border in CSS so there is something to look
 * at for the frame or two before the image resolves.
 */
export function CardBack({ label }: { label?: string }) {
  const [art, setArt] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    void cardBackUrl().then((url) => {
      if (alive) setArt(url)
    })
    return () => {
      alive = false
    }
  }, [])

  return (
    <div
      className="card-back grid size-full place-items-center"
      style={
        art
          ? {
              backgroundImage: `url(${art})`,
              // Stretched, not cropped: the texture's aspect follows the card
              // mesh's UVs rather than the DOM card's, and the 2% difference is
              // invisible while a crop would clip the frame off one edge.
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
            }
          : undefined
      }
    >
      <div className="text-center">
        {label && (
          <div className="eyebrow mt-2" style={{ fontSize: '0.5625rem' }}>
            {label}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * A held card, as it looks in the vault.
 *
 * The same object as `CardFace` above — same ground, same rarity border and
 * wash, same foil on the top two rarities — because a card you own should be
 * the card you pulled, not a different rendering of the same row.
 *
 * What differs is the bottom third, and it differs because the question has
 * changed. At the reveal you are asking *what did I hit*, so that card leads
 * with the multiple and the 24h move. Holding it, you are asking *what is it
 * worth now against what it was worth when I pulled it*, so this one leads
 * with current value and change since acquired. Those are also simply the
 * numbers a `Position` carries: it has no 24h delta or market cap on it, and
 * inventing either to fill the same slots would be worse than showing the
 * ones that are real.
 */
export function VaultCardFace({
  symbol,
  name,
  kind,
  rarity,
  quantity: qty,
  priceNow,
  valueCents,
  basisCents,
  changeCents,
}: {
  symbol: string
  name: string
  kind: 'stock' | 'coin' | 'crypto'
  rarity: Rarity
  quantity: number
  priceNow: number
  valueCents: number
  basisCents: number
  changeCents: number
}) {
  const foil = rarity === 'legendary' || rarity === 'mythic'
  const up = changeCents >= 0

  return (
    <div
      className={`card-face ${rarityClass(rarity)} ${foil ? 'card-foil' : ''} flex size-full flex-col`}
    >
      <div className="card-face__wash" aria-hidden />

      <header className="relative flex items-start justify-between gap-2 px-3 pt-3">
        <RarityChip rarity={rarity} />
        <KindBadge kind={kind} />
      </header>

      <div className="relative flex flex-col items-center px-3 pt-3">
        <AssetLogo kind={kind} symbol={symbol} rarity={rarity} size={52} />

        <div className="mt-2.5 w-full text-center">
          <div className="truncate text-lg leading-tight font-bold tracking-tight">{symbol}</div>
          <div
            className="mt-0.5 truncate text-[0.625rem] leading-tight"
            style={{ color: 'var(--color-muted)' }}
            title={name}
          >
            {name}
          </div>
        </div>
      </div>

      <div className="relative mt-auto px-3 pb-3">
        <div
          className="mb-2 flex items-baseline justify-between gap-2 border-t pt-2 text-[0.625rem]"
          style={{ color: 'var(--color-faint)' }}
        >
          <span className="tnum truncate">
            {quantity(qty)} {kind === 'stock' ? 'sh' : symbol}
          </span>
          <span className="tnum shrink-0">@ {usd(priceNow)}</span>
        </div>

        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="tnum truncate text-xl leading-none font-bold">
              {usd(valueCents / 100)}
            </div>
            <div className="tnum mt-1 text-[0.5625rem]" style={{ color: 'var(--color-faint)' }}>
              from {usd(basisCents / 100)}
            </div>
          </div>

          <div className="shrink-0 text-right">
            <div
              className="tnum text-sm leading-none font-bold"
              style={{ color: up ? 'var(--color-up)' : 'var(--color-down)' }}
            >
              {up ? '+' : '−'}
              {usd(Math.abs(changeCents) / 100)}
            </div>
            <div className="text-[0.5625rem]" style={{ color: 'var(--color-faint)' }}>
              since pulled
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
